import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Download, ShieldCheck, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { DevistoLogo } from "@/components/DevistoLogo";
import { SignaturePad } from "@/components/studio/SignaturePad";
import { API, beDate, inputCls, btnPrimary, apiError } from "@/lib/studioApi";

const Block = ({ title, children, testId }) => (
  <div data-testid={testId} className="mt-5">
    <p className="text-xs font-bold uppercase tracking-wider text-[#3A5CC8]">{title}</p>
    <div className="mt-2 space-y-1.5 text-sm text-slate-800">{children}</div>
  </div>
);

export default function PublicReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const pad = useRef(null);

  const load = () => {
    axios.get(`${API}/rapport/${token}`).then(({ data }) => setReport(data)).catch(() => setError(true));
  };

  useEffect(load, [token]);

  const sign = async () => {
    if (!name.trim()) return toast.error("Indiquez votre nom pour signer.");
    const signature_data = pad.current?.toDataURL() || "";
    if (!signature_data) return toast.error("Signez dans le cadre prévu.");
    setBusy(true);
    try {
      await axios.post(`${API}/rapport/${token}/sign`, { name, email, comment, signature_data });
      toast.success("Rapport signé. L'entreprise est prévenue.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Signature impossible."));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div data-testid="public-report-error" className="font-body min-h-screen bg-[#F1F5F9] flex flex-col items-center justify-center px-6 text-center">
        <XCircle className="w-10 h-10 text-red-500" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-[#0F2645]">Rapport introuvable</h1>
        <p className="mt-2 text-sm text-[#5C6A87]">Ce lien n'est plus valide.</p>
      </div>
    );
  }

  if (!report) return <div className="font-body min-h-screen bg-[#F1F5F9] flex items-center justify-center text-[#5C6A87]">Chargement…</div>;

  const hours = [report.arrival_time, report.departure_time].filter(Boolean).join(" – ");

  return (
    <div data-testid="public-report-page" className="font-body min-h-screen bg-[#F1F5F9] pt-12 pb-16 px-5">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <DevistoLogo className="h-7 w-auto" />
          <span className="text-xs font-semibold text-[#93A0BC]">Rapport {report.reference}</span>
        </div>

        <div className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <p className="text-xs font-bold uppercase tracking-wider text-[#3A5CC8]">{report.company.name}</p>
          <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-[#0F2645]">
            Rapport d'intervention {report.reference}
          </h1>
          <p className="mt-1 text-sm text-[#5C6A87]">
            {report.client_name} · {beDate(report.date)}{hours ? ` · ${hours}` : ""}
            {report.technician_name ? ` · ${report.technician_name}` : ""}
          </p>
          {report.site_address && <p className="text-sm text-[#5C6A87]">Chantier : {report.site_address}</p>}

          {!!report.works.length && (
            <Block title="Travaux réalisés" testId="report-works">
              {report.works.map((w, i) => (
                <p key={i} data-testid={`report-work-${i}`} className="flex items-start gap-2">
                  <CheckCircle2 className={`mt-0.5 w-4 h-4 shrink-0 ${w.done ? "text-green-600" : "text-slate-300"}`} />
                  <span>
                    {w.label}
                    {w.quantity ? ` (${w.quantity} ${w.unit || ""})` : ""}
                    {w.note ? ` — ${w.note}` : ""}
                  </span>
                </p>
              ))}
            </Block>
          )}

          {!!report.materials_used.length && (
            <Block title="Matériel utilisé" testId="report-materials">
              {report.materials_used.map((m, i) => (
                <p key={i}>• {m.label}{m.quantity ? ` × ${m.quantity} ${m.unit || ""}` : ""}</p>
              ))}
            </Block>
          )}

          {!!report.measures.length && (
            <Block title="Mesures relevées" testId="report-measures">
              {report.measures.map((m, i) => (
                <p key={i}>• {m.label} : {[m.value, m.unit].filter(Boolean).join(" ") || "—"}</p>
              ))}
            </Block>
          )}

          {report.observations && (
            <Block title="Observations" testId="report-observations"><p className="leading-relaxed">{report.observations}</p></Block>
          )}
          {report.recommendations && (
            <Block title="Recommandations" testId="report-recommendations"><p className="leading-relaxed">{report.recommendations}</p></Block>
          )}

          {!!report.photos.length && (
            <Block title="Photos de l'intervention" testId="report-photos">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {report.photos.map((p, i) => (
                  <figure key={i}>
                    <img src={p.url} alt={p.caption || "Photo de l'intervention"} loading="lazy" className="h-28 w-full rounded-xl object-cover" />
                    {p.caption && <figcaption className="mt-1 text-[11px] text-[#93A0BC]">{p.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </Block>
          )}

          <p className="mt-6 rounded-xl bg-[#F6F8FD] p-4 text-xs leading-relaxed text-[#5C6A87]">
            Ce rapport décrit les travaux réalisés. Les montants éventuels font l'objet d'un document séparé
            (devis ou facture).
          </p>

          <a
            href={`${API}/intervention/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="report-pdf-link"
            className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A5CC8] hover:text-[#2F4CA8] transition-colors duration-200"
          >
            <Download className="w-3.5 h-3.5" /> Télécharger le PDF
          </a>
        </div>

        {report.signature ? (
          <div data-testid="report-signed" className="mt-6 rounded-2xl border border-green-200 bg-green-50 p-6">
            <p className="font-heading text-lg font-bold text-green-800 inline-flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> Rapport signé
            </p>
            <p className="mt-1 text-sm text-green-800">
              Signé par {report.signature.name} le {beDate(report.signature.signed_at)}.
            </p>
          </div>
        ) : (
          <div data-testid="report-sign-block" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <h2 className="font-heading text-lg font-semibold text-[#0F2645]">Réception des travaux</h2>
            <p className="mt-1 text-sm text-[#5C6A87]">
              En signant, vous confirmez que les travaux décrits ci-dessus ont bien été réalisés.
            </p>
            <input data-testid="report-sign-name" className={`${inputCls} mt-4`} placeholder="Vos nom et prénom *" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="report-sign-email" type="email" className={`${inputCls} mt-3`} placeholder="Votre e-mail (facultatif)" value={email} onChange={(e) => setEmail(e.target.value)} />
            <textarea data-testid="report-sign-comment" rows={2} className={`${inputCls} mt-3`} placeholder="Une remarque ? (facultatif)" value={comment} onChange={(e) => setComment(e.target.value)} />

            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#5C6A87]">Signature</p>
            <SignaturePad ref={pad} testId="report-signature" className="mt-2" />

            <button type="button" data-testid="report-sign-btn" disabled={busy} onClick={sign} className={`${btnPrimary} mt-5`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Signer le rapport
            </button>
            <p className="mt-4 inline-flex items-start gap-2 text-[11px] leading-relaxed text-[#93A0BC]">
              <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
              Signature électronique horodatée (règlement eIDAS). Votre nom, la date et votre adresse IP sont
              conservés comme preuve, uniquement pour ce rapport.
            </p>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-[#93A0BC]">
          Rapport établi avec Devisto · {report.company.phone} · {report.company.email}
        </p>
      </div>
    </div>
  );
}
